import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getMcpServerEndpoint } from '@gitbook/env';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    DialogRoot,
    GitBookLoader,
    Tab,
    Tabs,
    toast,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import {
    applySiteTemplate,
    DeveloperPlainCodeBlock,
    GitBookMcpClientInstructions,
    GitBookMcpTokenInstructions,
    ImportContentDialog,
    type McpClient,
    McpClientSegmentedControl,
} from '../../Components';
import { useNavigation, useSetAnalyticsScreen } from '../../navigation';
import { apiClient, apiQuery, apiQueryClient } from '../../state';
import { useAddSpaceToEmptySite, useSiteScreenLoading } from '../Site/components';
import {
    type AIProvider,
    AIProviderLaunchButton,
    OnboardingAction,
    PromptBox,
    prompt,
    SiteOnboardingGitSyncDialog,
} from './components';

type SiteOnboardingPickerOptions = 'git-sync' | 'import' | 'template';
type SiteOnboardingStep =
    | { type: 'pick-start' }
    | { type: 'connect-ai-tool'; provider: AIProvider };

const SAMPLE_SITE_TITLE = 'Sample Docs';

/**
 * Render the site setup choices inside the welcome onboarding layout.
 */
export function SiteOnboardingScreen(props: { site: api.Site; organization: api.Organization }) {
    const { site, organization } = props;
    const { navigate } = useNavigation();
    const { isLoading, wrapLoading } = useSiteScreenLoading();
    const importDialogTrigger = useDialogTriggerState();
    const addSpaceToEmptySite = useAddSpaceToEmptySite({ organization, site });
    const [isGitSyncOpen, setIsGitSyncOpen] = React.useState(false);

    const navigateHome = useEventCallback(() => {
        navigate({ screen: { type: 'home', organization: organization.id } });
    });

    const refreshOrganizationContent = React.useCallback(async () => {
        await apiQueryClient.invalidateQueries({
            queryKey: apiQuery('getOrganizationAllContent', {
                organizationId: organization.id,
            }).queryKey,
        });
    }, [organization.id]);

    const refreshSiteStructure = React.useCallback(async () => {
        await apiQueryClient.invalidateQueries({
            queryKey: apiQuery('getSiteStructure', {
                organizationId: organization.id,
                siteId: site.id,
            }).queryKey,
        });
    }, [organization.id, site.id]);

    const applySampleTemplateToSite = useEventCallback(async (targetSite: api.Site) => {
        await Promise.all([
            apiClient.sites.updateSiteById(organization.id, targetSite.id, {
                title: SAMPLE_SITE_TITLE,
            }),
            applySiteTemplate({
                organizationId: organization.id,
                site: targetSite,
            }),
        ]);
    });

    const setupSampleDocsOnCurrentSite = useEventCallback(async () => {
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await applySampleTemplateToSite(site);
            }, 'Error while setting up sample docs');
            await refreshSiteStructure();
            await refreshOrganizationContent();
            navigateHome();
        });
    });

    const setupSampleDocsOnNewSite = useEventCallback(async () => {
        await wrapLoading(async () => {
            await toast.catch(async () => {
                const { data: newSite } = await apiClient.sites.createSite(organization.id, {
                    title: SAMPLE_SITE_TITLE,
                });
                await applySampleTemplateToSite(newSite);
            }, 'Error while setting up sample docs');
            await refreshOrganizationContent();
            navigateHome();
        });
    });

    const onPick = React.useCallback(
        (option: SiteOnboardingPickerOptions) => {
            switch (option) {
                case 'template':
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            {
                                type: 'site_content_create_intent',
                                screen: 'onboarding',
                                source: 'template',
                                templateKey: 'ultimate-template',
                            }
                        )
                    );
                    void setupSampleDocsOnCurrentSite();
                    return;
                case 'import':
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            {
                                type: 'site_content_create_intent',
                                screen: 'onboarding',
                                source: 'import',
                                sourceType: 'other',
                            }
                        )
                    );
                    importDialogTrigger.open();
                    return;
                case 'git-sync':
                    analytics.logEvent(
                        analytics.createSiteEvent(
                            { id: site.id, organization: organization.id },
                            {
                                type: 'site_content_create_intent',
                                screen: 'onboarding',
                                source: 'import',
                                sourceType: 'gitsync',
                            }
                        )
                    );
                    setIsGitSyncOpen(true);
                    return;
                default:
                    assertNever(option);
            }
        },
        [importDialogTrigger, organization.id, setupSampleDocsOnCurrentSite, site.id]
    );

    return (
        <>
            <SiteOnboardingPicker
                site={site}
                organization={organization}
                onPick={onPick}
                onSkip={setupSampleDocsOnCurrentSite}
                onGoToGitBook={setupSampleDocsOnNewSite}
                isLoading={isLoading}
            />

            <DialogRoot {...importDialogTrigger.rootProps}>
                <ImportContentDialog
                    title="Import your content"
                    trigger="site_empty_state"
                    target={{ site, organization: organization.id, siteOnboarding: true }}
                    onStart={async (target) => {
                        await toast.catch(async () => {
                            await addSpaceToEmptySite(target.space.id);
                            importDialogTrigger.close();
                            navigate({
                                screen: {
                                    type: 'space',
                                    space: target.space.id,
                                    organization: target.space.organization,
                                    changeRequest: target.changeRequestId,
                                },
                            });
                        }, 'Error while adding space to site');
                    }}
                />
            </DialogRoot>

            <SiteOnboardingGitSyncDialog
                site={site}
                organization={organization}
                isOpen={isGitSyncOpen}
                onOpenChange={setIsGitSyncOpen}
            />
        </>
    );
}

function SiteOnboardingPicker(props: {
    site: api.Site;
    organization: api.Organization;
    onPick: (option: SiteOnboardingPickerOptions) => void;
    onSkip: () => void;
    onGoToGitBook: () => void;
    isLoading: boolean;
}) {
    const { site, organization, onPick, onSkip, onGoToGitBook, isLoading } = props;
    const [step, setStep] = React.useState<SiteOnboardingStep>({ type: 'pick-start' });

    useSetAnalyticsScreen(
        isLoading
            ? null
            : analytics.createSiteEvent(
                  { id: site.id, organization: organization.id },
                  {
                      screen:
                          step.type === 'pick-start'
                              ? 'site_onboarding_pick_start'
                              : 'site_onboarding_connect_ai_tool',
                  }
              ),
        [isLoading, organization.id, site.id, step.type]
    );

    if (isLoading) {
        return (
            <div className="flex min-h-80 w-full flex-col items-center justify-center gap-4">
                <GitBookLoader />
                <div className="text-ui-heading-small">Preparing your workspace...</div>
            </div>
        );
    }

    switch (step.type) {
        case 'pick-start':
            return (
                <SiteOnboardingStepContent>
                    <div className="flex w-full flex-col gap-4">
                        <h2 className="heading-page">
                            <div className="text-base/60">Set up your site</div>
                            <div>How do you want to start?</div>
                        </h2>
                    </div>

                    <div className="my-3 flex flex-col gap-4">
                        <OnboardingAction
                            title="Sync a Git repository"
                            icon={Icon.GitHub}
                            onPress={() => onPick('git-sync')}
                        />
                        <OnboardingAction
                            title="Import from a website or files"
                            icon={Icon.Download}
                            onPress={() => onPick('import')}
                        />
                        <OnboardingAction
                            title="Start with a site template"
                            icon={Icon.SiteSection}
                            onPress={() => onPick('template')}
                        />
                    </div>

                    <SetupDocsSiteWithAI
                        site={site}
                        organization={organization}
                        onSkip={onSkip}
                        onLaunchAIProvider={(provider) =>
                            setStep({ type: 'connect-ai-tool', provider })
                        }
                    />
                </SiteOnboardingStepContent>
            );
        case 'connect-ai-tool':
            return (
                <ConnectAIToolStep
                    provider={step.provider}
                    onGoToGitBook={onGoToGitBook}
                    onBack={() => setStep({ type: 'pick-start' })}
                />
            );
        default:
            assertNever(step);
    }
}

function SiteOnboardingStepContent(props: { children: React.ReactNode }) {
    return (
        <div className="fade-in flex w-full min-w-0 animate-in flex-col gap-8 duration-300 ease-snappy-out lg:min-h-[36rem] lg:pt-16">
            {props.children}
        </div>
    );
}

function SetupDocsSiteWithAI(props: {
    site: api.Site;
    organization: api.Organization;
    onSkip: () => void;
    onLaunchAIProvider: (provider: AIProvider) => void;
}) {
    const { site, organization, onSkip, onLaunchAIProvider } = props;

    const trackAIContentCreateIntent = (sourceType: AIProvider | 'other') => {
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organization.id },
                {
                    type: 'site_content_create_intent',
                    screen: 'onboarding',
                    source: 'ai',
                    sourceType,
                }
            )
        );
    };

    const launchAIProvider = (provider: AIProvider) => {
        trackAIContentCreateIntent(provider);
        onLaunchAIProvider(provider);
    };

    return (
        <div className="flex flex-col gap-4 pt-1">
            <div className="flex flex-col gap-1">
                <p className="text-ui-heading-small">Set up your docs with AI</p>
                <Description className="text-base">
                    Use your favorite AI tool to set up your docs in just a few minutes
                </Description>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <AIProviderLaunchButton provider="claude" onLaunch={launchAIProvider} />
                <AIProviderLaunchButton provider="cursor" onLaunch={launchAIProvider} />
                <AIProviderLaunchButton provider="codex" onLaunch={launchAIProvider} />
            </div>
            <p className="text-center text-muted text-ui-base">or</p>
            <PromptBox prompt={prompt.generic} onCopy={() => trackAIContentCreateIntent('other')} />
            <div className="flex justify-center">
                <Button
                    kind="link"
                    label="Skip"
                    onPress={() => {
                        analytics.logEvent(
                            analytics.createSiteEvent(
                                { id: site.id, organization: organization.id },
                                {
                                    type: 'site_content_create_intent',
                                    screen: 'onboarding',
                                    source: 'skip',
                                }
                            )
                        );
                        onSkip();
                    }}
                />
            </div>
        </div>
    );
}

function ConnectAIToolStep(props: {
    provider: AIProvider;
    onBack: () => void;
    onGoToGitBook: () => void;
}) {
    const { provider, onBack, onGoToGitBook } = props;
    const [selectedTab, setSelectedTab] = React.useState<'mcp' | 'api-token'>('mcp');
    const initialClient = getMcpClientFromAIProvider(provider);
    const [selectedMcpClient, setSelectedMcpClient] = React.useState<McpClient>(initialClient);
    const [selectedTokenClient, setSelectedTokenClient] = React.useState<McpClient>(initialClient);
    const [token, setToken] = React.useState<string | null>(null);

    return (
        <SiteOnboardingStepContent>
            <div className="top-4 left-4 self-start justify-self-start lg:absolute">
                <Button label="Back" kind="blank" leadingIcon={Icon.ArrowLeft} onPress={onBack} />
            </div>

            <div className="flex w-full flex-col gap-4">
                <h2 className="heading-page">
                    <div className="text-base/60">Set up your site</div>
                    <div>Connect your AI tool</div>
                </h2>
            </div>

            <div className="flex w-full min-w-0 flex-col gap-6">
                <Tabs className="border-b">
                    <Tab
                        active={selectedTab === 'mcp'}
                        leadingIcon={Icon.MCP}
                        label="MCP"
                        onPress={() => setSelectedTab('mcp')}
                    />
                    <Tab
                        active={selectedTab === 'api-token'}
                        leadingIcon={Icon.Key}
                        label="API Token"
                        onPress={() => setSelectedTab('api-token')}
                    />
                </Tabs>

                <div className="grid min-h-[25rem] lg:w-[calc(100%+1rem)]">
                    <div
                        className={
                            selectedTab === 'mcp'
                                ? 'col-start-1 row-start-1'
                                : 'pointer-events-none invisible col-start-1 row-start-1'
                        }
                        aria-hidden={selectedTab !== 'mcp'}
                    >
                        <ConnectMcpPanel
                            selectedClient={selectedMcpClient}
                            onSelectedClientChange={setSelectedMcpClient}
                        />
                    </div>

                    <div
                        className={
                            selectedTab === 'api-token'
                                ? 'col-start-1 row-start-1'
                                : 'pointer-events-none invisible col-start-1 row-start-1'
                        }
                        aria-hidden={selectedTab !== 'api-token'}
                    >
                        <ConnectAPITokenPanel
                            token={token}
                            onTokenChange={setToken}
                            selectedClient={selectedTokenClient}
                            onSelectedClientChange={setSelectedTokenClient}
                        />
                    </div>
                </div>
            </div>

            <div className="mt-auto flex justify-end">
                <Button
                    kind="primary"
                    label="Go to GitBook"
                    trailingIcon={Icon.ArrowRight}
                    onPress={onGoToGitBook}
                />
            </div>
        </SiteOnboardingStepContent>
    );
}

function ConnectMcpPanel(props: {
    selectedClient: McpClient;
    onSelectedClientChange: (client: McpClient) => void;
}) {
    const { selectedClient, onSelectedClientChange } = props;
    const endpoint = getMcpServerEndpoint();

    return (
        <div className="flex w-full min-w-0 flex-col gap-7">
            <div className="flex min-h-16 flex-col gap-2">
                <Description>
                    GitBook's MCP server lets your AI agent, code editor, or terminal work with your
                    documentation: create and configure sites, open change requests, draft content,
                    edit pages, and restructure docs.
                </Description>
            </div>
            <div className="flex flex-col gap-2">
                <p className="text-ui-heading-small">Server URL</p>
                <Description>
                    Point your client at the GitBook MCP endpoint and authenticate with OAuth.
                </Description>
                <DeveloperPlainCodeBlock code={endpoint} syntax="bash" showLineNumbers={false} />
            </div>
            <div className="flex flex-col gap-4">
                <p className="text-ui-heading-small">Connect your client</p>
                <McpClientSegmentedControl
                    selectedClient={selectedClient}
                    onSelectedClientChange={onSelectedClientChange}
                />
                <div className="min-h-[10.5rem]">
                    <GitBookMcpClientInstructions
                        client={selectedClient}
                        endpoint={endpoint}
                        includeTokenInstructions={false}
                        showLineNumbers={false}
                        expandableCodeBlocks
                    />
                </div>
            </div>
        </div>
    );
}

function ConnectAPITokenPanel(props: {
    token: string | null;
    onTokenChange: (token: string) => void;
    selectedClient: McpClient;
    onSelectedClientChange: (client: McpClient) => void;
}) {
    const { token, onTokenChange, selectedClient, onSelectedClientChange } = props;
    const endpoint = getMcpServerEndpoint();

    return (
        <div className="flex w-full min-w-0 flex-col gap-7">
            <div className="flex min-h-16 flex-col gap-2">
                <Description>
                    API tokens let tools that cannot connect to MCP call the GitBook REST API with
                    your account permissions. Keep your token private and paste it only in tools you
                    trust.
                </Description>
            </div>
            <div className="flex flex-col gap-2">
                <p className="text-ui-heading-small">API Token</p>
                <Description>
                    Generate a token and pass it to your AI tool when prompted.
                </Description>
                <GenerateAPIToken token={token} onTokenChange={onTokenChange} />
            </div>
            <div className="flex flex-col gap-4">
                <p className="text-ui-heading-small">Use your token with an AI tool</p>
                <McpClientSegmentedControl
                    selectedClient={selectedClient}
                    onSelectedClientChange={onSelectedClientChange}
                />
                <div className="min-h-[10.5rem]">
                    <GitBookMcpTokenInstructions
                        client={selectedClient}
                        token={token ?? undefined}
                        endpoint={endpoint}
                        showLineNumbers={false}
                        expandableCodeBlocks
                    />
                </div>
            </div>
        </div>
    );
}

function GenerateAPIToken(props: { token: string | null; onTokenChange: (token: string) => void }) {
    const { token, onTokenChange } = props;
    const [isGeneratingToken, wrapGeneratingToken] = useLoadingState(false);

    const generateToken = async () => {
        await wrapGeneratingToken(async () => {
            await toast.catch(async () => {
                const response = await apiClient.users.createApiToken({
                    label: 'GitBook AI setup',
                });
                if (response.data.token) {
                    onTokenChange(response.data.token);
                }
            }, 'Error while generating API token');
        });
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex">
                <Button
                    kind="secondary"
                    label={token ? 'Generate new API token' : 'Generate API token'}
                    leadingIcon={Icon.Key}
                    onPress={generateToken}
                    loadable={[isGeneratingToken]}
                />
            </div>
            {token ? (
                <DeveloperPlainCodeBlock
                    code={token}
                    syntax="bash"
                    showLineNumbers={false}
                    expandable
                />
            ) : null}
        </div>
    );
}

function getMcpClientFromAIProvider(provider: AIProvider): McpClient {
    switch (provider) {
        case 'claude':
            return 'claude-code';
        case 'cursor':
            return 'cursor';
        case 'codex':
            return 'codex';
        default:
            assertNever(provider);
    }
}
